/* API */
import { api } from '../api';

/* Utils */
import { formatDateOnly } from './dateUtils';
import { trackSave } from './saveStatusUtils';

/* Broadcast */
import { broadcastChange } from '../socket';

export const tripUtils = {

    // Fetch all trips from backend
    getAllTrips: async () => {
        // Fetch real data from Backend
        const trips = await api.get('/trips');

        // Map DB (from_date) -> Frontend (fromDate)
        return trips.map(trip => {
            try {
                let columnConfig = trip.layout;
                if (typeof columnConfig === 'string') {
                    try {
                        columnConfig = JSON.parse(columnConfig);
                    } catch (e) {
                        console.error("Failed to parse trip layout:", e);
                    }
                }

                // Normalize columnConfig to always be an array (handle Seeded data vs New data)
                let normalizedColumns = columnConfig;
                if (columnConfig && !Array.isArray(columnConfig) && columnConfig.columns) {
                    normalizedColumns = columnConfig.columns;
                }

                // Normalize days layout if present
                const days = trip.days ? trip.days.map(day => {
                    let dayColumns = day.layout;
                    if (typeof dayColumns === 'string') {
                        try {
                            dayColumns = JSON.parse(dayColumns);
                        } catch (e) { console.error(e); }
                    }
                    return {
                        ...day,
                        columns: dayColumns || day.columns // prefer layout if exists
                    };
                }) : [];

                return {
                    ...trip,
                    fromDate: trip.from_date,
                    toDate: trip.to_date,
                    layout: normalizedColumns,
                    days: days
                };
            } catch (err) {
                console.error("Error mapping trip:", trip.id, err);
                return null;
            }
        }).filter(Boolean); // Filter out failed trips
    },

    // Fetch single trip details
    getTrip: async (tripId) => {
        const trip = await api.get(`/trips/${tripId}`);

        let columnConfig = trip.layout;
        if (typeof columnConfig === 'string') {
            try {
                columnConfig = JSON.parse(columnConfig);
            } catch (e) {
                console.error("Failed to parse trip layout:", e);
            }
        };

        // Normalize columnConfig to always be an array (handle Seeded data vs New data)
        let normalizedColumns = columnConfig;
        if (columnConfig && !Array.isArray(columnConfig) && columnConfig.columns) {
            normalizedColumns = columnConfig.columns;
        }

        // Normalize days layout if present
        const days = trip.days ? trip.days.map(day => {
            let dayColumns = day.layout;
            if (typeof dayColumns === 'string') {
                try {
                    dayColumns = JSON.parse(dayColumns);
                } catch (e) { console.error(e); }
            }
            return {
                ...day,
                columns: dayColumns || day.columns // prefer layout if exists
            };
        }) : [];

        return {
            ...trip,
            fromDate: trip.from_date,
            toDate: trip.to_date,
            layout: normalizedColumns,
            days: days,
            members: trip.members || [],
            invite: trip.invite || null
        };
    },

    // Save trip to backend
    saveTrip: async (trip) => {
        // Map Frontend (fromDate) -> DB (from_date)
        const payload = {
            ...trip,
            from_date: formatDateOnly(trip.fromDate),
            to_date: formatDateOnly(trip.toDate),
            locations: trip.location || trip.locations || [],
            layout: trip.layout
        };
        return await api.post('/trips', payload);
    },

    // Delete trip from backend
    deleteTrip: async (tripId) => {
        return await api.delete(`/trips/${tripId}`);
    },

    // Duplicate trip
    duplicateTrip: async (tripId) => {
        const trip = await api.post(`/trips/${tripId}/duplicate`);

        return {
            ...trip,
            fromDate: trip.from_date,
            toDate: trip.to_date,
            layout: trip.layout,
            days: [] // Empty days are fine for list view
        };
    },

    // Create an empty row with the given columns
    createEmptyRow: (cols) => {
        const newRow = { id: crypto.randomUUID(), _version: 1 };   // matches the server's default version

        cols.forEach(col => {
            // Empty arrays when initiating the tags
            if (col.type === 'tags') {
                newRow[col.id] = [];
            }
            // Null when initiating the price-split (since this cell stores a whole object (with amount, paidBy, splitWith, etc.) not a string)
            else if (col.type === 'price-split') {
                newRow[col.id] = null;
            }
            // Empty string for all other types
            else {
                newRow[col.id] = '';
            }
        });
        return newRow;
    },

    // ---- Granular autosave (editing an existing trip) ----

    // Rows (items)
    updateItem: (tripId, itemId, row, version, structureVersion) => {
        const { id: _id, _version, ...item_data } = row;   // strip id + _version, the rest is item_data
        return api.patch(`/trips/${tripId}/items/${itemId}`, { item_data, version, structureVersion });
    },
    createItem: (tripId, dayId, row) => {
        const { id, _version, ...item_data } = row;
        return api.post(`/trips/${tripId}/days/${dayId}/items`, { id, item_data });
    },
    deleteItem: (tripId, itemId) => api.delete(`/trips/${tripId}/items/${itemId}`),

    // Days
    createDay: (tripId, day) => api.post(`/trips/${tripId}/days`, day),
    updateDay: (tripId, dayId, fields) => api.patch(`/trips/${tripId}/days/${dayId}`, fields),
    deleteDay: (tripId, dayId) => api.delete(`/trips/${tripId}/days/${dayId}`),

    // Trip meta
    updateTripMeta: (tripId, fields) => api.patch(`/trips/${tripId}`, fields),

    // Sync orders
    syncItemOrder: (tripId, dayId, orderedIds) =>
        api.patch(`/trips/${tripId}/days/${dayId}/item-order`, { orderedIds }),
    syncDayOrder: (tripId, orderedIds) =>
        api.patch(`/trips/${tripId}/day-order`, { orderedIds }),

    deleteColumn: (tripId, { columnId, scope, dayId }) =>
        api.post(`/trips/${tripId}/columns/delete`, { columnId, scope, dayId }),

    changeColumnType: (tripId, { columnId, newType, placeholder, scope, dayId }) =>
        api.post(`/trips/${tripId}/columns/type`, { columnId, newType, placeholder, scope, dayId }),
};

// Wrap every trip MUTATION so the save-status indicator reflects them
['updateItem', 'createItem', 'deleteItem', 'createDay', 'updateDay', 'deleteDay', 'updateTripMeta', 'syncItemOrder', 'syncDayOrder', 'deleteColumn', 'changeColumnType', 'saveTrip']
    .forEach(name => {
        const original = tripUtils[name];
        tripUtils[name] = (...args) => {
            const promise = trackSave(original(...args));
            promise.then(() => broadcastChange()).catch(() => { });
            return promise;
        };
    });